import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["arena", "chips", "submit", "form", "hiddenInputs", "progressFill", "progressLabel", "templates", "template"]
  static values  = {
    max:                  { type: Number, default: 15 },
    progressTemplate:     String,
    hint:                 String,
    allDoneSomeTemplate:  String,
    allDoneNone:          String,
    allDoneHint:          String
  }

  connect() {
    this.added     = []
    this.queue     = this.templateTargets.slice(0, this.maxValue)
    this.pool      = this.templateTargets.slice(this.maxValue)
    this.dragState = { dragging: false, startX: 0, startY: 0, axis: null, currentX: 0, card: null }
    this.boundMove = this.dragMove.bind(this)
    this.boundEnd  = this.dragEnd.bind(this)

    document.addEventListener("mousemove", this.boundMove)
    document.addEventListener("touchmove", this.boundMove, { passive: false })
    document.addEventListener("mouseup", this.boundEnd)
    document.addEventListener("touchend", this.boundEnd)

    this.renderCard()
    this.updateProgress()
  }

  disconnect() {
    document.removeEventListener("mousemove", this.boundMove)
    document.removeEventListener("touchmove", this.boundMove)
    document.removeEventListener("mouseup", this.boundEnd)
    document.removeEventListener("touchend", this.boundEnd)
  }

  renderCard() {
    this.arenaTarget.replaceChildren()

    if (this.queue.length === 0 || this.added.length >= this.maxValue) {
      this.showAllDone()
      return
    }

    const hint = document.createElement("div")
    hint.className   = "flex items-center gap-2 text-[11px] text-gimmo-ink-muted mb-2.5 w-full justify-center tracking-wider uppercase font-bold"
    hint.textContent = this.hintValue
    this.arenaTarget.appendChild(hint)

    const template = this.queue[0]
    const card     = template.querySelector("[data-card]").cloneNode(true)
    this.arenaTarget.appendChild(card)

    const actions = document.createElement("div")
    actions.className = "flex gap-5 mt-4 justify-center"
    actions.innerHTML = `
      <button type="button" class="w-14 h-14 rounded-full border border-gimmo-border bg-gimmo-surface flex items-center justify-center cursor-pointer transition-transform hover:scale-110 hover:border-gimmo-border-hi" data-skip>
        <svg viewBox="0 0 24 24" class="w-3.5 h-3.5 stroke-gimmo-ink-muted fill-none" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
      <button type="button" class="w-14 h-14 rounded-full gimmo-bg-gradient flex items-center justify-center cursor-pointer transition-transform hover:scale-110 shadow-gimmo-glow" data-add>
        <svg viewBox="0 0 24 24" class="w-[22px] h-[22px]"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="white"/></svg>
      </button>`
    this.arenaTarget.appendChild(actions)

    this.dragState.card = card
    actions.querySelector("[data-add]").addEventListener("click", () => this.resolveCard(true))
    actions.querySelector("[data-skip]").addEventListener("click", () => this.resolveCard(false))
    this.setupDrag(card)
  }

  setupDrag(card) {
    card.addEventListener("mousedown", e => this.dragStart(e))
    card.addEventListener("touchstart", e => this.dragStart(e), { passive: true })
  }

  dragStart(e) {
    const point = e.touches ? e.touches[0] : e
    this.dragState.dragging = true
    this.dragState.axis     = e.touches ? null : "x"
    this.dragState.startX   = point.clientX
    this.dragState.startY   = point.clientY
    if (this.dragState.card && !e.touches) this.dragState.card.style.transition = "none"
  }

  dragMove(e) {
    if (!this.dragState.dragging || !this.dragState.card) return
    const point = e.touches ? e.touches[0] : e
    const dx    = point.clientX - this.dragState.startX
    const dy    = point.clientY - this.dragState.startY

    if (this.dragState.axis === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
      if (Math.abs(dx) <= Math.abs(dy)) {
        this.dragState.dragging = false
        return
      }
      this.dragState.axis = "x"
      this.dragState.card.style.transition = "none"
    }

    if (e.cancelable) e.preventDefault()

    this.dragState.currentX = dx
    const c   = this.dragState.card
    const cur = this.dragState.currentX
    c.style.transform = `translateX(${cur}px) rotate(${cur * 0.07}deg)`
    const add  = c.querySelector("[data-overlay-add]")
    const skip = c.querySelector("[data-overlay-skip]")
    const pct  = Math.abs(cur) / 120
    if (cur > 20)       { add.style.opacity = Math.min(1, pct);  skip.style.opacity = 0 }
    else if (cur < -20) { skip.style.opacity = Math.min(1, pct); add.style.opacity  = 0 }
    else                { add.style.opacity = 0; skip.style.opacity = 0 }
  }

  dragEnd() {
    if (!this.dragState.dragging || !this.dragState.card) return
    this.dragState.dragging = false
    const cur = this.dragState.currentX
    if (cur > 80) this.resolveCard(true)
    else if (cur < -80) this.resolveCard(false)
    else if (this.dragState.card) {
      this.dragState.card.style.transition = "transform .4s cubic-bezier(.34,1.56,.64,1)"
      this.dragState.card.style.transform  = ""
    }
    this.dragState.currentX = 0
  }

  resolveCard(add) {
    const card = this.dragState.card
    if (!card) return
    const dir = add ? 1 : -1
    card.style.transition = "transform .3s ease, opacity .3s ease"
    card.style.transform  = `translateX(${dir * 420}px) rotate(${dir * 10}deg)`
    card.style.opacity    = "0"
    const template = this.queue.shift()
    if (add && this.added.length < this.maxValue) {
      this.added.push({
        id:   template.dataset.productId,
        name: template.dataset.productName
      })
      this.addChip(template)
      this.updateProgress()
    } else if (!add && this.pool.length > 0) {
      this.queue.push(this.pool.shift())
    }
    setTimeout(() => this.renderCard(), 320)
  }

  addChip(template) {
    const chip = document.createElement("div")
    chip.className   = "text-[11px] font-bold px-3 py-1 rounded-full bg-[rgba(167,139,250,.1)] border border-[rgba(167,139,250,.2)] text-gimmo-accent"
    chip.textContent = template.dataset.productName
    this.chipsTarget.appendChild(chip)
  }

  updateProgress() {
    const n   = this.added.length
    const max = this.maxValue
    this.progressFillTarget.style.width = `${(n / max) * 100}%`
    if (this.hasProgressLabelTarget) {
      this.progressLabelTarget.textContent = (this.progressTemplateValue || `${n} / ${max}`).replace("__COUNT__", String(n))
    }
    this.submitTarget.disabled = false
    this.syncHiddenInputs()
  }

  syncHiddenInputs() {
    this.hiddenInputsTarget.replaceChildren()
    this.added.forEach(p => {
      const input = document.createElement("input")
      input.type  = "hidden"
      input.name  = "onboarding[liked_product_ids][]"
      input.value = p.id
      this.hiddenInputsTarget.appendChild(input)
    })
  }

  showAllDone() {
    const n = this.added.length
    this.arenaTarget.replaceChildren()

    const wrap = document.createElement("div")
    wrap.className = "text-center py-8"

    const icon = document.createElement("span")
    icon.className   = "inline-block text-[42px] mb-3.5 gimmo-text-gradient"
    icon.textContent = n > 0 ? "✦" : "◇"
    wrap.appendChild(icon)

    const summary = document.createElement("p")
    summary.className = "text-gimmo-ink-mid text-[13px] mb-1"
    if (n > 0) {
      summary.innerHTML = (this.allDoneSomeTemplateValue || "").replace("__COUNT__", `<b class="text-gimmo-accent">${n}</b>`)
    } else {
      summary.textContent = this.allDoneNoneValue
    }
    wrap.appendChild(summary)

    const hint = document.createElement("p")
    hint.className   = "text-gimmo-ink-muted text-[12px] mt-1.5"
    hint.textContent = this.allDoneHintValue
    wrap.appendChild(hint)

    this.arenaTarget.appendChild(wrap)
    this.submitTarget.disabled = false
    this.syncHiddenInputs()
  }

  skipAll() {
    this.formTarget.requestSubmit()
  }
}
